import { axios } from '@complispace/cs-design-system';

const baseURL = process.env.CONTENT_ENGINE_URL;
const usersBaseUrl = `${baseURL}/app/users`;
const authBaseUrl = `${baseURL}/app/auth`;

/**
 * GET /app/users/me
 * Used when logged in via Hub (Cognito) to get current user and bestRole.
 *
 * Response shape:
 * {
 *   hubUserId: string,
 *   hubUsername: string,
 *   hubActive: boolean,
 *   email: string,
 *   metadata: object,
 *   createdAt: string (ISO),
 *   updatedAt: string (ISO),
 *   securedModulePermissions: string[],
 *   groups: string[],
 *   roles: [{ roleId: number, name: string }],
 *   bestRole: number  // 100 = Complispace Administrator, 200 = Group Administrator, 300 = Client Administrator, 400 = Client User
 * }
 *
 * @returns {Promise<{ bestRole: number, ... }>}
 */
async function getCurrentUser() {
  const response = await axios.get(`${usersBaseUrl}/me`);
  const data = response.data || {};
  const bestRole = data.bestRole;

  if (bestRole === undefined || bestRole === null) {
    throw new Error('Invalid user response: missing bestRole from /app/users/me');
  }

  return data;
}

/**
 * POST /app/auth/logout/initiate
 * Invalidates the cache and publishes a logoutInitiated event to the Hub event bus,
 * cascading logout to all products for single sign-out.
 *
 * Response shape:
 * {
 *   source: string,
 *   userId: string,
 *   username: string,
 *   organisationId: string
 * }
 *
 * @returns {Promise<{ source: string, userId: string, username: string, organisationId: string }>}
 */
async function initiateLogout() {
  const response = await axios.post(`${authBaseUrl}/logout/initiate`);
  return response.data;
}

/**
 * GET /app/users/user-list
 * Used to get all users in the system.
 *
 * Response shape:
 *
 * {
 *   clientId: number,
 *   appId: number,
 *   userList: [{
 *     hubUserId: string,
 *     hubUsername: string,
 *     hubActive: boolean,
 *     email: string,
 *     metadata: object,
 *     createdAt: string (ISO),
 *     updatedAt: string (ISO),
 *     roles: [{
 *       roleId: number,
 *       name: string
 *     }],
 *     bestRole: {
 *       roleId: number,
 *       name: string
 *     },
 *     securedModulePermissions: string[],
 *     groups: string[]
 *   }]
 * }
 */
async function getUserList() {
  const response = await axios.get(`${usersBaseUrl}/user-list`);
  return response.data;
}

/**
 * GET /app/users/permissions
 * Returns available roles, groups, and secured module permissions for the current app context.
 *
 * Response shape:
 * {
 *   roles: [{ roleId: number, name: string }],
 *   groups: string[],
 *   securedModulePermissions: string[]
 * }
 *
 * @returns {Promise<{ roles: Array, groups: string[], securedModulePermissions: string[] }>}
 */
async function getUserPermissions() {
  const response = await axios.get(`${usersBaseUrl}/permissions`);
  return response.data;
}

/**
 * PUT /app/user/roles/{hubUserId}
 * Update roles, groups and secured module permissions for a user within the current client/app context.
 *
 * @param {string} hubUserId
 * @param {{ roleIds: number[], groups: string[], securedModulePermissions: string[] }} payload
 */
async function updateUserRoles(hubUserId, { roleIds, groups, securedModulePermissions }) {
  const response = await axios.put(`${usersBaseUrl}/roles/${hubUserId}`, {
    roleIds,
    groups,
    securedModulePermissions
  });
  return response.data;
}

/**
 * PUT /app/users/roles
 * Bulk update roles, groups and secured module permissions for multiple users.
 * Only fields present in the payload will be updated; omitted fields remain unchanged.
 *
 * @param {{ hubUserIds: string[], roleIds?: number[], groups?: string[], securedModulePermissions?: string[] }} payload
 * @returns {Promise<{ summary: { total: number, updated: number, failed: number }, roles: object, updated: object[], failed: object[] }>}
 */
async function bulkUpdateUserRoles(payload) {
  const response = await axios.put(`${usersBaseUrl}/roles`, payload);
  return response.data;
}

const users = {
  getCurrentUser,
  initiateLogout,
  getUserList,
  getUserPermissions,
  updateUserRoles,
  bulkUpdateUserRoles
};

export default users;
